import {
  Select,
  TextField,
  DateRangePicker,
  DatepickerSimple,
  AVAILABLE_TIMEZONES,
  Checkbox,
  Timestamp,
  Timezone,
  toBool,
} from 'vopay';
import { Box, withStyles, type TransactionHistoryStatus } from 'vopay';
import { FC, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Transactions.module.scss';
import { useClientData } from '../../../providers/ClientData.provider';
import { Button } from 'UI';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import { usePermissions } from '@/providers/Permissions.provider';

const cn = withStyles(styles);

type TransactionSearchParams = {
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  settlementDate: Timestamp | undefined;
  includeSubAccounts: boolean;
  timeRange: [Timestamp, Timestamp];
  timeZone: Timezone;
};

type SearchFormProps = {
  type: string;
  status: TransactionHistoryStatus;
  flagged: string;
  currency: string;
  searchTerm: string;
  timeZone: Timezone;
  balances: Balance[];
  initialTimeRange: [Timestamp, Timestamp];
  hasInvalidEndDate: boolean;
  shouldShowEndDateError: boolean;
  includeSubAccounts: boolean;
  settlementDate: Timestamp | undefined;
  setShouldShowEndDateError: (event: boolean) => void;
  onSearch: (searchParams: TransactionSearchParams) => void;
};

const TransactionSearchForm: FC<SearchFormProps> = ({
  type,
  status,
  flagged,
  currency,
  searchTerm,
  initialTimeRange,
  hasInvalidEndDate,
  shouldShowEndDateError,
  timeZone,
  includeSubAccounts,
  settlementDate,
  balances,
  setShouldShowEndDateError,
  onSearch,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.TransactionHistory.TransactionSearchForm' });
  const { response } = useClientData();
  const { permissions } = usePermissions();
  const [currentType, setCurrentType] = useState<string>(type);
  const [currentStatus, setCurrentStatus] = useState<TransactionHistoryStatus>(status);
  const [currentFlagged, setCurrentFlagged] = useState<string>(flagged);
  const [currentCurrency, setCurrentCurrency] = useState<string>(currency);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[Timestamp, Timestamp]>(initialTimeRange);
  const [currentTimeZone, setCurrentTimeZone] = useState<Timezone>(timeZone);
  const [currentIncludeSubAccounts, setCurrentIncludeSubAccounts] = useState<boolean>(includeSubAccounts);
  const [currentSettlementDate, setCurrentSettlementDate] = useState<Timestamp | undefined>(settlementDate);
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];
  const typeOptions: { [key: string]: string } = {
    All: t('typeOptions.all'),
    'EFT Withdrawal': t('typeOptions.eftWithdrawal'),
    'EFT Funding': t('typeOptions.eftFunding'),
    'VoPayInstant Withdraw': t('typeOptions.voPayInstantWithdraw'),
    Refund: t('typeOptions.refund'),
    'Currency Conversion': t('typeOptions.currencyConversion'),
    'CrossBorder Withdrawal': t('typeOptions.crossBorderWithdrawal'),
    'Interac Money Request': t('typeOptions.interacMoneyRequest'),
    'Interac Bulk Payout': t('typeOptions.interacBulkPayout'),
    'Inbound e-Transfer': t('typeOptions.inboundETransfer'),
    'Visa Direct Push': t('typeOptions.visaDirectPush'),
    'Credit Card': t('typeOptions.creditCard'),
    'Google Pay': t('typeOptions.googlePay'),
    'PayPal Withdrawal': t('typeOptions.payPalWithdrawal'),
    'Account Transfer': t('typeOptions.accountTransfer'),
    Reversal: t('typeOptions.reversal'),
    'Bill Pay': t('typeOptions.billPay'),
    'Auto Balance Transfer': t('typeOptions.autoBalanceTransfer'),
    'Virtual Account Deposit': t('typeOptions.virtualAccountDeposit'),
    'Virtual Account Funding': t('typeOptions.virtualAccountFunding'),
    'Virtual Account Withdraw': t('typeOptions.virtualAccountWithdraw'),
    'Virtual Account Wire': t('typeOptions.virtualAccountWire'),
    'Other Funding': t('typeOptions.otherFunding'),
    'Other Withdrawal': t('typeOptions.otherWithdrawal'),
  };
  const typeOptionsUS: { [key: string]: string } = {
    All: t('typeOptions.all'),
    'ACH Withdrawal': t('typeOptions.achWithdrawal'),
    'ACH Funding': t('typeOptions.achFunding'),
    'RTP Withdrawal': t('typeOptions.rtpWithdrawal'),
    'Fedwire Withdrawal': t('typeOptions.fedwireWithdrawal'),
    'Fedwire Funding': t('typeOptions.fedwireFunding'),
    Refund: t('typeOptions.refund'),
    'Currency Conversion': t('typeOptions.currencyConversion'),
    'Visa Direct Push': t('typeOptions.visaDirectPush'),
    'Credit Card': t('typeOptions.creditCard'),
    'Google Pay': t('typeOptions.googlePay'),
    'PayPal Withdrawal': t('typeOptions.payPalWithdrawal'),
    'Venmo Withdrawal': t('typeOptions.venmoWithdrawal'),
    'Account Transfer': t('typeOptions.accountTransfer'),
    Reversal: t('typeOptions.reversal'),
    'Auto Balance Transfer': t('typeOptions.autoBalanceTransfer'),
    'Virtual Account Deposit': t('typeOptions.virtualAccountDeposit'),
    'Virtual Account Funding': t('typeOptions.virtualAccountFunding'),
    'Virtual Account Withdraw': t('typeOptions.virtualAccountWithdraw'),
    'Virtual Account Wire': t('typeOptions.virtualAccountWire'),
    'Other Funding': t('typeOptions.otherFunding'),
    'Other Withdrawal': t('typeOptions.otherWithdrawal'),
  };

  const statusOptions: Record<Exclude<TransactionHistoryStatus, null>, string> = {
    All: t('statusOptions.all'),
    requested: t('statusOptions.requested'),
    pending: t('statusOptions.pending'),
    'in progress': t('statusOptions.inProgress'),
    sent: t('statusOptions.sent'),
    successful: t('statusOptions.successful'),
    complete: t('statusOptions.complete'),
    settled: t('statusOptions.settled'),
    failed: t('statusOptions.failed'),
    cancelled: t('statusOptions.cancelled'),
    'cancellation requested': t('statusOptions.cancellationRequested'),
    'pending approval': t('statusOptions.pendingApproval'),
    waiting: t('statusOptions.waiting'),
  };

  const flaggedOptions = {
    All: t('flaggedOptions.all'),
    Yes: t('flaggedOptions.yes'),
    No: t('flaggedOptions.no'),
  };

  const currencyOptions = useMemo(() => {
    const options: { [key: string]: string } = {
      All: t('currencyOptions.all'),
    };

    balances.forEach((balance) => {
      options[balance.Currency] = balance.Currency;
    });

    return options;
  }, [balances]);
  return (
    <Box flx="col" gapSize="16px">
      <div className={cn('firstRow')}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range); // Update local state for time range
          }}
          initialTime={timeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />
        <div className={cn('timezone')}>
          <Select
            version="2"
            placeholder={t('timeZone') || undefined}
            options={timeZoneOptions}
            name="timeZone"
            value={currentTimeZone}
            onBlur={() => {}}
            filterable
            onChange={(e) => {
              setCurrentTimeZone(e.target.value as Timezone);
            }}
            sort={false}
          />
        </div>
      </div>
      <div className={cn('secondRow')}>
        <div className={cn('defaultWidth')}>
          <Select
            version="2"
            placeholder={t('currency') || undefined}
            options={convertToOptions(currencyOptions)}
            name="currency"
            value={currentCurrency}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentCurrency(e.target.value);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth', 'type')}>
          <Select
            version="2"
            placeholder={t('type') || undefined}
            options={response.Country === 'US' ? convertToOptions(typeOptionsUS) : convertToOptions(typeOptions)}
            name="type"
            value={currentType}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentType(e.target.value);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth', 'status')}>
          <Select
            version="2"
            placeholder={t('status') || undefined}
            options={convertToOptions(statusOptions)}
            name="status"
            value={currentStatus || ''}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentStatus(e.target.value as TransactionHistoryStatus);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth')}>
          <Select
            version="2"
            placeholder={t('flagged') || undefined}
            options={convertToOptions(flaggedOptions)}
            name="flagged"
            value={currentFlagged}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentFlagged(e.target.value);
            }}
            sort={false}
          />
        </div>

        <div className={cn('defaultWidth', 'settlementDate')}>
          <DatepickerSimple
            variant="outlineShadow"
            value={(currentSettlementDate || '').toString()}
            selected={currentSettlementDate}
            format="timestamp"
            position="bottomLeft"
            onChange={(e) => {
              setCurrentSettlementDate(e.target.value ? JSON.parse(e.target.value) : undefined);
            }}
            onBlur={() => {}}
            name="settlementDate"
            placeholder="Settlement Date"
            inputWidth="auto"
            className={cn('settlementDatePicker')}
            allowBlank
          />
        </div>

        <div className={cn('defaultWidth')}>
          <TextField
            key="TransactionSearchKeyword"
            name="TransactionSearchKeyword"
            placeholder={t('keyword') || ''}
            value={currentSearchTerm}
            onBlur={() => {}}
            onChange={(e) => {
              setCurrentSearchTerm(e.target.value);
            }}
            variant="outlineShadow"
          />
        </div>

        <Button
          id="search-transaction-history"
          size="small"
          colorScheme="primary"
          onClick={() => {
            onSearch({
              type: currentType,
              status: currentStatus,
              flagged: currentFlagged,
              currency: currentCurrency,
              searchTerm: currentSearchTerm,
              timeRange,
              timeZone: currentTimeZone,
              includeSubAccounts: currentIncludeSubAccounts,
              settlementDate: currentSettlementDate,
            });
          }}
        >
          {t('search')}
        </Button>

        {toBool(response.SubAccountPermissions) && !!permissions.ViewSubaccounts && (
          <Box width="40%" jc="fs" maxheight="38px" base="8" pb="sm" pt="sm">
            <Checkbox
              label={t('includeSubAccounts') || ''}
              name={'IncludeSubAccount'}
              variant="border"
              value={currentIncludeSubAccounts}
              onBlur={() => {}}
              onChange={(e) => {
                setCurrentIncludeSubAccounts(e.target.checked);
              }}
              labelSize="16px"
              labelPosition="right"
              hasNoPadding
              disabled={false}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};
function convertToOptions(options: { [key: string]: string }) {
  return Object.entries(options).map(([key, value]) => ({
    label: key,
    value: value,
  }));
}

export default TransactionSearchForm;
